import {ReactElement, useEffect, useMemo, useState} from "react";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Property} from "../../../../contexts/properties/logic";
import {useProperties} from "../../../../contexts/properties/PropertiesContext";
import {Well} from "@zendeskgarden/react-notifications";
import {Field, Input, Label, Message} from "@zendeskgarden/react-forms";
import {CustomZDDropdown} from "../../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {Button} from "@zendeskgarden/react-buttons";
import {AcquisitionProperty} from "../../types";
import * as css from "./styles/CopytToNormalModal.module.scss";
import {useAcquisitionCopyToNormalMutation} from "../../../../__generated__/generated_types";
import {toast} from "react-toastify";

export interface CopyToNormalModalProps {
    acquisitionProperty: AcquisitionProperty;
    onModalClose: () => void;
}

export function CopyToNormalModal(props: CopyToNormalModalProps): ReactElement {
    const {allNormalProperties} = useProperties();
    const [normalPropertyId, setNormalPropertyId] = useState<string>("");
    const [normalPropertyIdTouched, setNormalPropertyIdTouched] = useState<boolean>(false);
    const [budgetYear, setBudgetYear] = useState<string>("");
    const [budgetYearTouched, setBudgetYearTouched] = useState<boolean>(false);
    const [invalidBudgetYear, setInvalidBudgetYear] = useState<boolean>(false);

    const [copyToNormal, {loading: copyToNormalLoading}] = useAcquisitionCopyToNormalMutation({
        notifyOnNetworkStatusChange: true
    });

    const propMap = useMemo(() => {
        if(allNormalProperties) {
            return new Map(allNormalProperties.map(np => [np.id, np]));
        }
        return new Map<string, Property>();
    }, allNormalProperties);

    useEffect(() => {
        if(normalPropertyId) {
            const normalProperty = propMap.get(normalPropertyId);
            if(normalProperty && budgetYear === "") {
                setBudgetYear(normalProperty.budgetYear.toString());
            }
        }
    }, [normalPropertyId]);

    useEffect(() => {
        const to = setTimeout(() => {
            if(budgetYear) {
                try {
                    const year = parseInt(budgetYear);
                    const invalid = props.acquisitionProperty.acquisitionBudgetYear !== year &&
                            props.acquisitionProperty.acquisitionBudgetYear !== (year - 1);
                    setInvalidBudgetYear(invalid);
                } catch {
                    setInvalidBudgetYear(true);
                }
            }
        }, 350);
        return () => clearTimeout(to);
    }, [budgetYear]);

    function doCopyToNormal(): void {
        let localBudgetYear: number | null = null;
        try {
            localBudgetYear = parseInt(budgetYear);
        } catch { /* DO NOTHING */ }
        if(!localBudgetYear) {
            return;
        }

        copyToNormal({
            variables: {
                acquisitionPropertyId: props.acquisitionProperty.id,
                normalPropertyId: normalPropertyId,
                normalBudgetYear: localBudgetYear
            }
        }).then(res => {
            if(res.data?.copyAcquisitionToBudget) {
                toast.success("Budget copied successfully.");
                props.onModalClose();
            }
        }).catch(() => {
            toast.error("Error copying budget.");
        });
    }


    const normalProperties = allNormalProperties ?? [];
    return <Modal className={css.modal} onClose={props.onModalClose}>
        <Header>Copy {props.acquisitionProperty.name} Budget</Header>
        <Body>
            <Well className={css.well}>
                Budget values are copied as manual values into the selected property. <span>Any manual values for the
                selected property will be deleted.</span>
            </Well>
            <form>
                <CustomZDDropdown
                    applySelectedItems={item => {
                        if(Array.isArray(item) && item.length === 1) {
                            setNormalPropertyId(item[0]);
                            setNormalPropertyIdTouched(true);
                        } else if(typeof item === "string") {
                            setNormalPropertyId(item);
                            setNormalPropertyIdTouched(true);
                        }
                    }}
                    openDropdownPlaceholder="Select Property"
                    closedDropdownPlaceholder={undefined}
                    options={normalProperties.map(np => ({value: np.id, label: np.name}))}
                    isError={normalPropertyIdTouched && !normalPropertyId}
                    errorMessage={"You must select a property."}
                    label={"Property"}
                />
                <Field className={css.field}>
                    <Label>Budget Year</Label>
                    <Input type="number" value={budgetYear} onChange={e => {
                        try {
                            const value = parseFloat(e.target.value);
                            setBudgetYear(Math.round(value).toString());
                        } catch(e) {/* DO NOTHING */}
                        setBudgetYearTouched(true);
                    }} />
                    {budgetYearTouched && !budgetYear &&
                        <Message validation="error">You must provide a budget year.</Message>
                    }
                    {invalidBudgetYear &&
                        <Message validation="error">Budget years do not overlap. No information would be copied.</Message>
                    }
                </Field>
            </form>
        </Body>
        <Footer>
            <FooterItem>
                <Button isDanger isBasic onClick={props.onModalClose}>Cancel</Button>
            </FooterItem>
            <FooterItem>
                <Button
                    isPrimary
                    disabled={!normalPropertyId || !budgetYear || invalidBudgetYear || copyToNormalLoading}
                    onClick={doCopyToNormal}>
                    Copy
                </Button>
            </FooterItem>
        </Footer>
        <Close aria-label="Close copy to normal modal" />
    </Modal>;
}